import React from 'react';

export const AID = {
  gold:      '#c9a227',
  goldSoft:  '#d4a548',
  goldDeep:  '#a8821a',
  goldLight: '#e8c662',
  brownDeep: '#6b3f15',
  brown:     '#8a5a2b',
  cream:     '#fbf5d6',
};

export function StarSparkle({ size = 12, color = AID.gold, opacity = 1, style = {} }) {
  return (
    <svg width={size} height={size} viewBox="-50 -50 100 100" style={{ opacity, display: 'block', ...style }}>
      <path d="M0,-48 L10,-10 L48,0 L10,10 L0,48 L-10,10 L-48,0 L-10,-10 Z" fill={color} />
    </svg>
  );
}

export function Crescent({ size = 60, color = AID.gold, glow = false, style = {} }) {
  const uid = React.useId().replace(/:/g, 'x');
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ overflow: 'visible', display: 'block', ...style }}>
      <defs>
        <mask id={`cres-${uid}`}>
          <rect x="0" y="0" width="100" height="100" fill="white" />
          <circle cx="62" cy="42" r="32" fill="black" />
        </mask>
      </defs>
      {glow && <circle cx="50" cy="50" r="42" fill={color} opacity="0.18" filter="blur(6px)" />}
      <circle cx="50" cy="50" r="38" fill={color} mask={`url(#cres-${uid})`} />
    </svg>
  );
}

export function CrescentStar({ size = 80, color = AID.gold, glow = false, style = {} }) {
  return (
    <div style={{ position: 'relative', width: size, height: size, ...style }}>
      <Crescent size={size} color={color} glow={glow} />
      <div style={{ position: 'absolute', top: size * 0.18, left: size * 0.62 }}>
        <StarSparkle size={size * 0.22} color={color} />
      </div>
    </div>
  );
}

export function Lantern({ size = 90, color = AID.gold, glowColor = AID.goldLight, style = {} }) {
  return (
    <svg width={size} height={size * 1.6} viewBox="0 0 100 160" style={{ display: 'block', ...style }}>
      <line x1="50" y1="0"   x2="50" y2="18"  stroke={color} strokeWidth="1.5" />
      <path d="M40,18 L60,18 L62,28 L38,28 Z" fill={color} />
      <rect x="44" y="28" width="12" height="6" fill={color} />
      <path d="M30,38 Q22,55 22,80 Q22,105 30,118 L70,118 Q78,105 78,80 Q78,55 70,38 Z"
        fill={color} opacity="0.22" stroke={color} strokeWidth="1.8" />
      <ellipse cx="50" cy="78" rx="20" ry="32" fill={glowColor} opacity="0.55" />
      <line x1="30" y1="55"  x2="70" y2="55"  stroke={color} strokeWidth="1.2" opacity="0.7" />
      <line x1="28" y1="78"  x2="72" y2="78"  stroke={color} strokeWidth="1.2" opacity="0.7" />
      <line x1="30" y1="102" x2="70" y2="102" stroke={color} strokeWidth="1.2" opacity="0.7" />
      <line x1="50" y1="38"  x2="50" y2="118" stroke={color} strokeWidth="1"   opacity="0.5" />
      <g transform="translate(50,78)">
        <path d="M0,-8 L2,-2 L8,0 L2,2 L0,8 L-2,2 L-8,0 L-2,-2 Z" fill={color} opacity="0.9" />
      </g>
      <rect x="44" y="118" width="12" height="6" fill={color} />
      <path d="M38,124 L62,124 L60,134 L40,134 Z" fill={color} />
      <line x1="50" y1="134" x2="50" y2="148" stroke={color} strokeWidth="1.5" />
      <circle cx="50" cy="152" r="4" fill={color} />
    </svg>
  );
}

export function ZelligeCorner({ size = 180, color = AID.gold, opacity = 0.25, flip = 'tl', style = {} }) {
  const transforms = { tl: '', tr: 'scaleX(-1)', bl: 'scaleY(-1)', br: 'scale(-1,-1)' };
  return (
    <svg width={size} height={size} viewBox="0 0 200 200"
      style={{ opacity, transform: transforms[flip], display: 'block', ...style }}>
      <g fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round">
        <path d="M0,0 L40,0 M0,0 L0,40" />
        <circle cx="0" cy="0" r="60" />
        <circle cx="0" cy="0" r="90" />
        <circle cx="0" cy="0" r="120" />
        {[0, 15, 30, 45, 60, 75, 90].map(a => (
          <line key={a} x1="0" y1="0"
            x2={120 * Math.cos(a * Math.PI / 180)}
            y2={120 * Math.sin(a * Math.PI / 180)} opacity="0.5" />
        ))}
        {[20, 45, 70].map(a => {
          const r = 75;
          const x = r * Math.cos(a * Math.PI / 180);
          const y = r * Math.sin(a * Math.PI / 180);
          return (
            <g key={a} transform={`translate(${x},${y})`}>
              <rect x="-5" y="-5" width="10" height="10" fill={color} opacity="0.6" />
              <rect x="-5" y="-5" width="10" height="10" transform="rotate(45)" fill={color} opacity="0.6" />
            </g>
          );
        })}
      </g>
    </svg>
  );
}

export function ZelligeBg({ color = AID.gold, opacity = 0.06 }) {
  const tile = encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">` +
    `<g fill="none" stroke="${color}" stroke-width="0.7">` +
    `<path d="M20,0 L20,40 M0,20 L40,20"/>` +
    `<circle cx="20" cy="20" r="9"/>` +
    `<rect x="15" y="15" width="10" height="10"/>` +
    `<rect x="15" y="15" width="10" height="10" transform="rotate(45 20 20)"/>` +
    `</g></svg>`
  );
  return (
    <div style={{
      position: 'absolute', inset: 0,
      backgroundImage: `url("data:image/svg+xml,${tile}")`,
      opacity,
      pointerEvents: 'none',
    }} />
  );
}

export function AidCalligraphy({ size = 36, color = AID.brownDeep, style = {} }) {
  return (
    <div dir="rtl" style={{
      fontFamily: "'Amiri', 'Georgia', serif",
      fontSize: size,
      color,
      fontWeight: 700,
      lineHeight: 1,
      letterSpacing: 1,
      userSelect: 'none',
      ...style,
    }}>
      عيد الأضحى مبارك
    </div>
  );
}

export function Divider({ color = AID.gold, width = 200, style = {} }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width, ...style }}>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(to right, transparent, ${color})` }} />
      <StarSparkle size={10} color={color} />
      <div style={{ flex: 1, height: 1, background: `linear-gradient(to left, transparent, ${color})` }} />
    </div>
  );
}
